import { processPath } from "@/services/backend/processes/lib/process-path";
import t from "@/lang/lang";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { formatDate } from "@/shared/lib/utilities/date";
import {
  ScrollBodyTable,
  ScrollBodyTableHeader,
} from "@/shared/components/tables/table-scroll-body";
import { ScrollArea, ScrollBar } from "@/shared/components/ui/scroll-area";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { NavLink } from "react-router";
import { Star } from "lucide-react";
import { RFC3339timestamp } from "@bitory-gmbh/nidavellir";

export interface ProcessResult {
  id: string;
  kind: GSMServiceKind | null;
  number: string;
  customerId: string;
  customerName: string;
  companyId: string;
  companyName: string;
  topic: string;
  createdAt: string;
  topResult: boolean;
  opportunityData: ProcessResultOpportunityData[];
}

export type ProcessResultOpportunityData = {
  opportunityId: string;
  updatedAt: RFC3339timestamp;
  createdAt: RFC3339timestamp;
};

export function ProcessResults({
  processes,
  expanded = false,
}: {
  processes: ProcessResult[];
  expanded?: boolean;
}) {
  const enableScroll = !expanded && processes.length > 4;

  return (
    <div>
      <div className="flex justify-between">
        <h5 className="text-lg font-bold">{t("Vorgänge")}</h5>
        <span className="text-muted-foreground text-sm">
          {processes.length} {t("gefunden")}
        </span>
      </div>
      <ScrollArea className={enableScroll ? "h-[20vh] border-b" : ""}>
        <ScrollBodyTable>
          <ScrollBodyTableHeader>
            <TableRow>
              <TableHead>{t("Top")}</TableHead>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead className="w-full">{t("Thema")}</TableHead>
              <TableHead>{t("Kunde")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Erstellt am")}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {t("VC Erstellt am")}
              </TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </ScrollBodyTableHeader>
          <TableBody>
            {processes.map((process) => (
              <ProcessResultTableRow key={process.id} process={process} />
            ))}
            {processes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-2 text-center">
                  {t("Keine Vorgänge gefunden")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollBodyTable>
        {enableScroll && <ScrollBar forceMount />}
      </ScrollArea>
    </div>
  );
}

function ProcessResultTableRow({ process }: { process: ProcessResult }) {
  return (
    <TableRow>
      <TableCell className="py-0 whitespace-nowrap">
        {process.topResult && <Star className="h-5 w-5" />}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">{process.number}</TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        {process.topic.length < 50
          ? process.topic
          : `${process.topic.substring(0, 50)}...`}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        {process.customerName}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        {formatDate(process.createdAt)}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        <span className="flex gap-2">
          {process.opportunityData.map((o) => (
            <span key={o.opportunityId}>{formatDate(o.createdAt)}</span>
          ))}
        </span>
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        <NavigateToProcess process={process} />
      </TableCell>
    </TableRow>
  );
}

function NavigateToProcess({ process }: { process: ProcessResult }) {
  const { id, kind } = process;

  const path = processPath(kind, id);

  return (
    <NavLink to={path}>
      <Button variant="ghost" size="sm">
        <span>{t("Navigieren")}</span>
      </Button>
    </NavLink>
  );
}
